// Simplified HTML Downloader for Knowledge Graph
// Based on the reference HTML structure

import { generateAdvancedFeatures } from './htmlDownloaderAdvanced.js';

export function downloadKnowledgeGraphHTML(graphData, analysisProgress, inputText) {
  const htmlContent = generateSimpleHTML(graphData, analysisProgress, inputText);
  const blob = new Blob([htmlContent], { type: 'text/html' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'knowledge-graph-analysis.html';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export function generateSimpleHTML(graphData, analysisProgress, inputText) {
  const cost = calculateCost(analysisProgress);
  const advancedFeatures = generateAdvancedFeatures();
  
  return `<!DOCTYPE html>
<html lang="en">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>Knowledge Graph Analysis</title>
    <script src="https://cdn.tailwindcss.com"></script>
    <script>
        tailwind.config = {
            theme: {
                extend: {
                    colors: {
                        indigo: {
                            50: '#eef2ff',
                            100: '#e0e7ff',
                            200: '#c7d2fe',
                            300: '#a5b4fc',
                            400: '#818cf8',
                            500: '#6366f1',
                            600: '#4f46e5',
                            700: '#4338ca',
                            800: '#3730a3',
                            900: '#312e81'
                        }
                    }
                }
            }
        }
    </script>
    <style>
        .cursor-grab { cursor: grab; }
        .cursor-grabbing { cursor: grabbing; }
        .animate-spin { animation: spin 1s linear infinite; }
        @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
        }
        .select-none { user-select: none; }
        .pointer-events-none { pointer-events: none; }
        .backdrop-blur-sm { backdrop-filter: blur(4px); }
        .bg-gradient-to-br {
            background-image: linear-gradient(to bottom right, var(--tw-gradient-stops));
        }
        .bg-gradient-to-r {
            background-image: linear-gradient(to right, var(--tw-gradient-stops));
        }
        .bg-clip-text {
            background-clip: text;
            -webkit-background-clip: text;
        }
        .text-transparent {
            color: transparent;
        }
    </style>
</head>
<body class="min-h-screen bg-gradient-to-br from-indigo-50 via-purple-50 to-cyan-50">
    <!-- Header -->
    <div class="bg-white shadow-lg border-b border-indigo-200">
        <div class="max-w-7xl mx-auto px-6 py-4">
            <div class="flex items-center justify-between">
                <div class="flex items-center gap-3">
                    <div class="p-2 bg-gradient-to-r from-indigo-600 to-purple-600 rounded-lg">
                        <svg class="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"></path>
                        </svg>
                    </div>
                    <div>
                        <h1 class="text-2xl font-bold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">
                            Knowledge Graph Analysis
                        </h1>
                        <p class="text-sm text-indigo-600 font-medium">Advanced narrative analysis and visualization</p>
                    </div>
                </div>
            </div>
        </div>
    </div>

    <div class="max-w-7xl mx-auto p-6">
        <!-- Analysis Information -->
        <div class="bg-white rounded-xl shadow-lg p-6 mb-6 border border-indigo-200">
            <h2 class="text-lg font-semibold text-indigo-900 mb-4">Analysis Information</h2>
            <div class="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div class="p-4 bg-emerald-50 rounded-lg border border-emerald-200">
                    <div class="text-2xl font-bold text-emerald-600">${analysisProgress.timeElapsed || '0'}s</div>
                    <div class="text-sm text-emerald-700">Time Elapsed</div>
                </div>
                <div class="p-4 bg-blue-50 rounded-lg border border-blue-200">
                    <div class="text-2xl font-bold text-blue-600">${analysisProgress.tokensUsed || '0'}</div>
                    <div class="text-sm text-blue-700">Tokens Used</div>
                </div>
                <div class="p-4 bg-purple-50 rounded-lg border border-purple-200">
                    <div class="text-2xl font-bold text-purple-600">¥${cost.toFixed(4)}</div>
                    <div class="text-sm text-purple-700">Estimated Cost</div>
                </div>
                <div class="p-4 bg-indigo-50 rounded-lg border border-indigo-200">
                    <div class="text-2xl font-bold text-indigo-600">${graphData.nodes?.length || 0}</div>
                    <div class="text-sm text-indigo-700">Entities Found</div>
                </div>
            </div>
        </div>

        <!-- Text Input Section -->
        <div class="bg-white rounded-xl shadow-lg p-6 mb-6 border border-indigo-200">
            <div class="flex items-center justify-between mb-4">
                <h2 class="text-lg font-semibold text-indigo-900 flex items-center gap-2">
                    <svg class="w-5 h-5 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"></path>
                    </svg>
                    Analyzed Text
                </h2>
            </div>
            
            <textarea class="w-full h-32 p-4 border-2 border-indigo-200 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent resize-none text-gray-700" readonly>${inputText || 'No text analyzed'}</textarea>
        </div>

        <!-- Graph Visualization Section -->
        <div class="bg-white rounded-xl shadow-lg p-6 border border-indigo-200">
            <div class="flex items-center justify-between mb-4">
                <h3 class="text-lg font-semibold text-indigo-900 flex items-center gap-2">
                    <svg class="w-5 h-5 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 11H5m14-7l2 2-2 2m-14 3l2 2-2 2"></path>
                    </svg>
                    Knowledge Graph Visualization
                </h3>
                <div class="flex items-center gap-4 text-sm text-indigo-600">
                    <div class="flex items-center gap-1">
                        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 11.5V14m0-2.5v-6a1.5 1.5 0 113 0m-3 6a1.5 1.5 0 00-3 0v2a7.5 7.5 0 0015 0v-5a1.5 1.5 0 00-3 0m-6-3V11m0-5.5v-1a1.5 1.5 0 013 0v1m0 0V11m0-5.5a1.5 1.5 0 013 0v3"></path>
                        </svg>
                        <span>Drag to pan</span>
                    </div>
                    <div class="flex items-center gap-1">
                        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 15l-2 5L9 9l11 4-5 2zm0 0l5 5M7.188 2.239l.777 2.897M5.136 7.965l-2.898-.777M13.95 4.05l-2.122 2.122m-5.657 5.656l-2.12 2.122"></path>
                        </svg>
                        <span>Click to select</span>
                    </div>
                    <div class="flex items-center gap-1">
                        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"></path>
                        </svg>
                        <span>Use arrows to rotate</span>
                    </div>
                </div>
            </div>
            
            <div class="relative border-2 border-indigo-300 rounded-lg overflow-hidden bg-gradient-to-br from-indigo-100 via-purple-100 to-cyan-100">
                <!-- View Controls Panel -->
                <div class="absolute bottom-4 left-4 z-20 bg-white/95 backdrop-blur-sm rounded-lg shadow-lg p-3 border-2 border-indigo-200">
                    <h4 class="font-semibold text-sm text-indigo-900 mb-2 flex items-center gap-1">
                        <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"></path>
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"></path>
                        </svg>
                        View Controls
                    </h4>
                    
                    <!-- Auto Rotate Toggle -->
                    <button id="autoRotateBtn" class="w-full flex items-center justify-center gap-1 px-2 py-1 rounded text-xs transition-colors mb-2 bg-indigo-100 hover:bg-indigo-200 text-indigo-700">
                        <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M14.828 14.828a4 4 0 01-5.656 0M9 10h1.586a1 1 0 01.707.293l2.414 2.414a1 1 0 00.707.293H15M13 16h-1.586a1 1 0 01-.707-.293l-2.414-2.414a1 1 0 00-.707-.293H7"></path>
                        </svg>
                        <span id="autoRotateText">Auto Rotate</span>
                    </button>

                    <!-- Zoom Controls -->
                    <div class="flex items-center gap-1 mb-2">
                        <button id="zoomInBtn" class="p-1 bg-indigo-100 hover:bg-indigo-200 rounded transition-colors">
                            <svg class="w-3 h-3 text-indigo-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7"></path>
                            </svg>
                        </button>
                        <span id="zoomDisplay" class="text-xs text-indigo-600 flex-1 text-center min-w-12 font-medium">100%</span>
                        <button id="zoomOutBtn" class="p-1 bg-indigo-100 hover:bg-indigo-200 rounded transition-colors">
                            <svg class="w-3 h-3 text-indigo-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM13 10H7"></path>
                            </svg>
                        </button>
                    </div>

                    <!-- Reset View -->
                    <button id="resetBtn" class="w-full flex items-center justify-center gap-1 px-2 py-1 bg-purple-50 hover:bg-purple-100 text-purple-700 rounded transition-colors text-xs font-medium">
                        <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"></path>
                        </svg>
                        Reset All
                    </button>
                </div>

                <!-- Rotation Controls -->
                <button id="rotateUpBtn" class="absolute top-2 left-1/2 transform -translate-x-1/2 z-10 p-2 bg-white/90 hover:bg-white shadow-lg rounded-lg transition-all duration-200 hover:scale-110 border border-indigo-200">
                    <svg class="w-5 h-5 text-indigo-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 15l7-7 7 7"></path>
                    </svg>
                </button>

                <button id="rotateDownBtn" class="absolute bottom-2 left-1/2 transform -translate-x-1/2 z-10 p-2 bg-white/90 hover:bg-white shadow-lg rounded-lg transition-all duration-200 hover:scale-110 border border-indigo-200">
                    <svg class="w-5 h-5 text-indigo-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"></path>
                    </svg>
                </button>

                <button id="rotateLeftBtn" class="absolute left-2 top-1/2 transform -translate-y-1/2 z-10 p-2 bg-white/90 hover:bg-white shadow-lg rounded-lg transition-all duration-200 hover:scale-110 border border-indigo-200">
                    <svg class="w-5 h-5 text-indigo-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7"></path>
                    </svg>
                </button>

                <button id="rotateRightBtn" class="absolute right-2 top-1/2 transform -translate-y-1/2 z-10 p-2 bg-white/90 hover:bg-white shadow-lg rounded-lg transition-all duration-200 hover:scale-110 border border-indigo-200">
                    <svg class="w-5 h-5 text-indigo-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"></path>
                    </svg>
                </button>

                <div class="absolute top-2 right-2 z-10 flex gap-1">
                    <button id="rotateCCWBtn" class="p-2 bg-white/90 hover:bg-white shadow-lg rounded-lg transition-all duration-200 hover:scale-110 border border-indigo-200">
                        <svg class="w-4 h-4 text-indigo-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"></path>
                        </svg>
                    </button>
                    <button id="rotateCWBtn" class="p-2 bg-white/90 hover:bg-white shadow-lg rounded-lg transition-all duration-200 hover:scale-110 border border-indigo-200">
                        <svg class="w-4 h-4 text-indigo-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20 20v-5h-.582M4.642 13A8.001 8.001 0 0020.582 15m-.582 0H15m5-5v-5h-.582m0 0a8.003 8.003 0 00-15.357 2m15.357-2H15"></path>
                        </svg>
                    </button>
                </div>

                <!-- SVG Canvas -->
                <svg id="graphSvg" width="100%" height="600" viewBox="0 0 800 600" class="cursor-grab">
                    <defs>
                        <marker id="arrowhead" markerWidth="10" markerHeight="7" refX="9" refY="3.5" orient="auto">
                            <polygon points="0 0, 10 3.5, 0 7" fill="#94a3b8"/>
                        </marker>
                    </defs>
                    <g id="graphGroup"></g>
                </svg>
            </div>

            <!-- Status Display -->
            <div class="mt-4 flex items-center justify-center gap-6 text-sm text-indigo-600 font-medium">
                <span>X: <span id="rotationX">0</span>°</span>
                <span>Y: <span id="rotationY">0</span>°</span>
                <span>Z: <span id="rotationZ">0</span>°</span>
                <span>Zoom: <span id="zoomStatus">100</span>%</span>
                <span id="autoRotateStatus" class="hidden">
                    <svg class="w-4 h-4 inline animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"></path>
                    </svg>
                    Auto-rotating
                </span>
            </div>
        </div>

        <!-- Statistics Dashboard -->
        <div class="mt-8 bg-white rounded-xl shadow-lg p-6 border border-indigo-200">
            <h3 class="text-xl font-semibold text-indigo-900 mb-4">Knowledge Graph Statistics</h3>
            <div class="grid grid-cols-2 sm:grid-cols-4 gap-4">
                <div class="text-center p-4 bg-emerald-50 rounded-lg border border-emerald-200">
                    <div id="charCount" class="text-2xl font-bold text-emerald-600">0</div>
                    <div class="text-sm text-emerald-700">Characters</div>
                </div>
                <div class="text-center p-4 bg-blue-50 rounded-lg border border-blue-200">
                    <div id="placeCount" class="text-2xl font-bold text-blue-600">0</div>
                    <div class="text-sm text-blue-700">Places</div>
                </div>
                <div class="text-center p-4 bg-purple-50 rounded-lg border border-purple-200">
                    <div id="objectCount" class="text-2xl font-bold text-purple-600">0</div>
                    <div class="text-sm text-purple-700">Concepts</div>
                </div>
                <div class="text-center p-4 bg-indigo-50 rounded-lg border border-indigo-200">
                    <div id="relCount" class="text-2xl font-bold text-indigo-600">0</div>
                    <div class="text-sm text-indigo-700">Relationships</div>
                </div>
            </div>
        </div>
    </div>

    <script>
        // Global state
        let graphData = ${JSON.stringify(graphData || {})};
        
        // Initialize the app
        document.addEventListener('DOMContentLoaded', function() {
            console.log('Initializing Knowledge Graph HTML...');
            console.log('Graph data:', graphData);
            
            if (graphData && graphData.nodes && graphData.nodes.length > 0) {
                renderGraph();
                updateStatistics();
            } else {
                console.log('No graph data available, creating sample data...');
                createSampleData();
                renderGraph();
                updateStatistics();
            }
            
            // Initialize advanced controls
            initializeAdvancedControls();
        });

        function createSampleData() {
            graphData = {
                nodes: [
                    { id: 'elara', name: 'Elara', type: 'protagonist', description: 'A kind young weaver with nimble fingers and a pure heart', importance: 10, x: -100, y: -50, z: 0, radius: 22 },
                    { id: 'grandmother', name: 'Grandmother', type: 'mentor', description: 'Wise woman who teaches weaving and speaks of kindness and wood wisdom', importance: 8, x: 100, y: -50, z: 0, radius: 18 },
                    { id: 'cottage', name: 'Small Cottage', type: 'dwelling', description: 'Elara\'s cozy home on the edge of the mysterious woods', importance: 6, x: -50, y: 100, z: 0, radius: 13 },
                    { id: 'whispering_woods', name: 'Whispering Woods', type: 'magical_realm', description: 'Mysterious forest full of magic, mystery, and secrets', importance: 9, x: 50, y: 100, z: 0, radius: 20 },
                    { id: 'loom', name: 'Weaving Loom', type: 'craft_tool', description: 'Elara\'s rhythmic tool for creating beautiful tapestries', importance: 7, x: 0, y: -100, z: 0, radius: 15 },
                    { id: 'kindness', name: 'Kindness', type: 'virtue', description: 'The most important quality that opens woodland secrets', importance: 10, x: 150, y: 0, z: 0, radius: 22 }
                ],
                links: [
                    { source: 'elara', target: 'grandmother', type: 'lives with', strength: 9, width: 4.5 },
                    { source: 'grandmother', target: 'elara', type: 'teaches craft', strength: 8, width: 4 },
                    { source: 'elara', target: 'loom', type: 'uses daily', strength: 8, width: 4 },
                    { source: 'elara', target: 'kindness', type: 'possesses', strength: 10, width: 5 },
                    { source: 'cottage', target: 'whispering_woods', type: 'borders', strength: 7, width: 3.5 },
                    { source: 'kindness', target: 'whispering_woods', type: 'unlocks', strength: 10, width: 5 }
                ]
            };
        }

        function renderGraph() {
            console.log('Rendering graph...');
            const graphGroup = document.getElementById('graphGroup');
            if (!graphGroup) {
                console.error('Graph group not found');
                return;
            }
            
            graphGroup.innerHTML = '';

            if (!graphData.nodes.length) {
                console.log('No nodes to render');
                return;
            }

            const width = 800;
            const height = 600;
            const centerX = width / 2;
            const centerY = height / 2;

            // Create transform group with zoom and translation
            const transformGroup = document.createElementNS('http://www.w3.org/2000/svg', 'g');
            transformGroup.setAttribute('transform', \`translate(\${centerX + translation.x}, \${centerY + translation.y}) scale(\${zoom})\`);

            // Render links
            graphData.links.forEach((link, index) => {
                const sourceNode = graphData.nodes.find(n => n.id === link.source);
                const targetNode = graphData.nodes.find(n => n.id === link.target);
                if (!sourceNode || !targetNode) return;

                const dx = targetNode.x - sourceNode.x;
                const dy = targetNode.y - sourceNode.y;
                const distance = Math.sqrt(dx * dx + dy * dy);

                if (distance === 0) return;

                const normalizedDx = dx / distance;
                const normalizedDy = dy / distance;

                const sourceRadius = sourceNode.radius;
                const targetRadius = targetNode.radius;

                const startX = sourceNode.x + normalizedDx * sourceRadius;
                const startY = sourceNode.y + normalizedDy * sourceRadius;
                const endX = targetNode.x - normalizedDx * targetRadius;
                const endY = targetNode.y - normalizedDy * targetRadius;

                // Create line
                const line = document.createElementNS('http://www.w3.org/2000/svg', 'line');
                line.setAttribute('x1', startX);
                line.setAttribute('y1', startY);
                line.setAttribute('x2', endX);
                line.setAttribute('y2', endY);
                line.setAttribute('stroke', '#94a3b8');
                line.setAttribute('stroke-width', link.width || 2);
                line.setAttribute('stroke-opacity', 0.7);
                line.setAttribute('marker-end', 'url(#arrowhead)');
                transformGroup.appendChild(line);

                // Add relationship label
                if (distance > 60) {
                    const text = document.createElementNS('http://www.w3.org/2000/svg', 'text');
                    text.setAttribute('x', (startX + endX) / 2);
                    text.setAttribute('y', (startY + endY) / 2);
                    text.setAttribute('text-anchor', 'middle');
                    text.setAttribute('font-size', '11px');
                    text.setAttribute('fill', '#475569');
                    text.setAttribute('fill-opacity', 0.8);
                    text.setAttribute('class', 'pointer-events-none select-none font-medium');
                    text.textContent = link.type;
                    transformGroup.appendChild(text);
                }
            });

            // Render nodes
            graphData.nodes.forEach(node => {
                // Create circle
                const circle = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
                circle.setAttribute('cx', node.x);
                circle.setAttribute('cy', node.y);
                circle.setAttribute('r', node.radius);
                circle.setAttribute('fill', getNodeColor(node.type));
                circle.setAttribute('stroke', '#ffffff');
                circle.setAttribute('stroke-width', 3);
                circle.setAttribute('class', 'cursor-pointer hover:stroke-indigo-500 transition-all duration-200');
                transformGroup.appendChild(circle);

                // Add label
                const text = document.createElementNS('http://www.w3.org/2000/svg', 'text');
                text.setAttribute('x', node.x);
                text.setAttribute('y', node.y + node.radius + 18);
                text.setAttribute('text-anchor', 'middle');
                text.setAttribute('font-size', '13px');
                text.setAttribute('fill', '#1e293b');
                text.setAttribute('class', 'pointer-events-none select-none font-semibold');
                text.textContent = node.name;
                transformGroup.appendChild(text);
            });

            graphGroup.appendChild(transformGroup);
            console.log('Graph rendered successfully');
        }

        function getNodeColor(type) {
            const colors = {
                'protagonist': '#10b981',
                'mentor': '#3b82f6',
                'dwelling': '#06b6d4',
                'magical_realm': '#16a34a',
                'setting': '#8b5cf6',
                'craft_tool': '#dc2626',
                'artwork': '#eab308',
                'materials': '#7c3aed',
                'virtue': '#ec4899',
                'knowledge': '#0ea5e9',
                'mystery': '#64748b'
            };
            return colors[type] || '#6b7280';
        }

        function updateStatistics() {
            const charTypes = ['protagonist', 'mentor'];
            const placeTypes = ['dwelling', 'magical_realm', 'setting'];
            const objectTypes = ['craft_tool', 'artwork', 'materials', 'virtue', 'knowledge', 'mystery'];

            document.getElementById('charCount').textContent = 
                graphData.nodes.filter(n => charTypes.includes(n.type)).length;
            document.getElementById('placeCount').textContent = 
                graphData.nodes.filter(n => placeTypes.includes(n.type)).length;
            document.getElementById('objectCount').textContent = 
                graphData.nodes.filter(n => objectTypes.includes(n.type)).length;
            document.getElementById('relCount').textContent = graphData.links.length;
        }

        ${advancedFeatures}
    </script>
</body>
</html>`;
}

function calculateCost(analysisProgress) {
  // DeepSeek pricing (per million tokens)
  const standardHourlyRates = {
    inputCacheHit: 0.5,    // 0.5元 per million tokens (cache hit)
    inputCacheMiss: 2.0,   // 2元 per million tokens (cache miss)
    output: 8.0            // 8元 per million tokens (output)
  };

  const tokensUsed = analysisProgress.tokensUsed || 0;
  
  // Estimate token distribution (simplified)
  const inputTokens = Math.floor(tokensUsed * 0.7); // 70% input tokens
  const outputTokens = Math.floor(tokensUsed * 0.3); // 30% output tokens
  
  // Estimate cache hit rate (simplified)
  const cacheHitRate = 0.3; // 30% cache hit rate
  const inputCacheHit = Math.floor(inputTokens * cacheHitRate);
  const inputCacheMiss = inputTokens - inputCacheHit;
  
  // Calculate costs
  const inputCacheHitCost = (inputCacheHit / 1000000) * standardHourlyRates.inputCacheHit;
  const inputCacheMissCost = (inputCacheMiss / 1000000) * standardHourlyRates.inputCacheMiss;
  const outputCost = (outputTokens / 1000000) * standardHourlyRates.output;
  
  return inputCacheHitCost + inputCacheMissCost + outputCost;
} 